import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { CheckCircle2, RotateCcw, X, FileSignature, Camera } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";

/** SignatureCompleteModal — captures completion photos (≥1, mandatory)
 * + optional customer signature. Posts to POST /work-orders/{id}/complete
 * with {signature?, customer_name?, completion_photos: string[]}. */
export default function SignatureCompleteModal({ workOrderId, onComplete, onClose }) {
  const sigRef = useRef(null);
  const [name, setName] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const clearSig = () => sigRef.current?.clear();

  const submit = async (withSignature) => {
    if (photos.length < 1) {
      toast.error("Please upload at least one photo of the completed work");
      return;
    }
    let signature = "";
    let customer_name = "";
    if (withSignature) {
      if (!sigRef.current || sigRef.current.isEmpty()) {
        toast.error("Please ask the customer to sign");
        return;
      }
      if (!name.trim()) {
        toast.error("Customer name required for signature");
        return;
      }
      signature = sigRef.current.getCanvas().toDataURL("image/png");
      customer_name = name.trim();
    }
    setSaving(true);
    try {
      await api.post(`/work-orders/${workOrderId}/complete`, {
        completion_photos: photos,
        ...(signature ? { signature, customer_name } : {}),
      });
      toast.success(withSignature ? "Work order signed & complete ✓" : "Work order complete ✓");
      onComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto" onClick={onClose} data-testid="sig-modal">
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-lg w-full overflow-hidden my-8" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E2E8F0] dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-heading text-xl text-[#0A2540] dark:text-white flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#C9A356]" /> Close work order
          </h3>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Required: Photos */}
          <div className="border-2 border-dashed border-[#C9A356] rounded-md p-4 bg-[#FFFBF0] dark:bg-amber-900/10">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-4 h-4 text-[#C9A356]" />
              <h4 className="font-bold text-sm text-[#0A2540] dark:text-white">
                Photo of completed work <span className="text-red-500">*required</span>
              </h4>
            </div>
            <p className="text-xs text-[#475569] dark:text-slate-400 mb-3">
              Upload at least 1 photo of the finished work (3+ recommended: before, during, after).
            </p>
            <PhotoUploader photos={photos} onChange={setPhotos} max={6} label="Completion photos" />
            <div className="text-[11px] text-[#94A3B8] mt-2">
              {photos.length === 0 ? "No photos yet" : `${photos.length} photo${photos.length === 1 ? "" : "s"} attached`}
            </div>
          </div>

          {/* Optional: Signature */}
          <div className="border border-[#E2E8F0] dark:border-slate-700 rounded-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileSignature className="w-4 h-4 text-[#C9A356]" />
              <h4 className="font-bold text-sm text-[#0A2540] dark:text-white">Customer signature <span className="text-[#94A3B8] font-normal">(optional)</span></h4>
            </div>
            <p className="text-xs text-[#475569] dark:text-slate-400 mb-3">
              Hand the device to the customer to confirm the work.
            </p>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Customer's full name" data-testid="sig-name"
              className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm mb-2"
            />
            <div className="border-2 border-dashed border-[#C9A356]/40 rounded-md bg-white touch-none" data-testid="sig-pad">
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{ width: 500, height: 180, className: "w-full" }}
              />
            </div>
            <button onClick={clearSig} className="text-xs text-[#475569] hover:text-[#0A2540] inline-flex items-center gap-1 mt-2" data-testid="sig-clear">
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button onClick={() => submit(false)} disabled={saving || photos.length === 0}
                    className="btn-outline-navy text-sm flex-1 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="complete-no-sig">
              Complete without signature
            </button>
            <button onClick={() => submit(true)} disabled={saving || photos.length === 0}
                    className="btn-accent text-sm flex-1 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="sig-complete">
              <CheckCircle2 className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Complete & sign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
